<script lang="ts" setup>
import type { MonospacePageMeta } from '#layers/spaces/types/space';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from '#app/composables/router';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { useCurrentPageNotRendered } from '#layers/spaces/composables/use-current-page-not-rendered';
import { useCurrentSpace } from '#layers/spaces/composables/use-current-space';
import { usePageActions } from '#layers/spaces/composables/use-page-actions';
import { isSpacePageRoute } from '#layers/spaces/utils/routes';
import { translatePage } from '#layers/spaces/utils/space';
import { BreadcrumbSwitcherButton, useFoldedSwitcherPopover } from '#layers/ui/public';
import { useWorkspaceStore } from '#layers/workspace/public';

interface Props {
	folded?: boolean;
}

const { folded = false } = defineProps<Props>();

const i18n = useI18n();
const store = useWorkspaceEntityStore();
const route = useRoute();

const { createPage, editPage, confirmDeletePage } = usePageActions();
const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
const { currentSpaceTranslated } = useCurrentSpace();
const { currentPageNotRendered } = useCurrentPageNotRendered();

const currentSpace = computed(() =>
	currentSpaceTranslated.value
	?? currentPageNotRendered.value?.space
	?? (route.params.spaceId ? store.MonospaceSpace.peekFirst(route.params.spaceId as string) : undefined),
);

const { data: space } = await store.MonospaceSpace.query((q) => q.first(currentSpace.value?.id
	? {
			key: currentSpace.value.id,
			include: { pages: {} },
			fetchPolicy: 'cache-only',
		}
	: { enabled: false }));

const pages = computed(() => space.value?.pages ?? []);

const currentPage = computed(() => {
	if (currentPageNotRendered.value) return currentPageNotRendered.value;

	const matched = route.matched.find((record) => isSpacePageRoute(record));
	return matched?.meta.page;
});

const resolvedCurrentPage = computed(() =>
	currentPage.value
		? store.MonospacePage.peekFirst(currentPage.value.id!)
		: undefined,
);

const currentSectionId = computed(() => pages.value.find((p) => p.id === currentPage.value?.id)?.sectionId);

const canEditOrCreate = computed(() => !!currentPage.value);

const { switcherContent } = useFoldedSwitcherPopover(() => folded);
</script>

<template>
	<SpaceOrPageSelector
		v-if="pages.length > 0"
		:items="pages.filter((page) => !page.isHidden)"
		:current-item="currentPage"
		:can-edit="canEditOrCreate"
		:can-create="canEditOrCreate"
		:item-props="{
			getId: (page) => page?.id ?? '',
			getTitle: (page) => page ? translatePage(page as MonospacePageMeta, i18n).title : '',
			getIcon: (page) => page?.icon ?? undefined,
			getRoute: (page) => ({
				name: page?.id,
				params: { workspace: currentWorkspaceSlug },
			}),
		}"
		:labels="{
			edit: i18n.t('page.edit_page'),
			create: i18n.t('space.add_new_page'),
			delete: i18n.t('space.delete_page_label'),
			group: i18n.t('workspace.breadcrumb.pages'),
		}"
		:tooltips="{
			create: i18n.t('page.cannot_create'),
			delete: i18n.t('page.cannot_delete'),
			edit: i18n.t('page.cannot_edit'),
		}"
		:placeholder="i18n.t('common.search')"
		:content="switcherContent"
		@edit="() => {
			if (resolvedCurrentPage) {
				editPage(resolvedCurrentPage);
			}
		}"
		@create="() => {
			if (currentSpace) {
				createPage(currentSpace.id, currentSectionId);
			}
		}"
		@delete="async () => {
			if (!currentPage) return;
			const page = store.MonospacePage.peekFirst(currentPage.id!);
			if (page) confirmDeletePage(page);
		}"
	>
		<template #default="{ open, currentTitle }">
			<BreadcrumbSwitcherButton
				:folded
				:label="currentTitle"
				:pressed="open"
			/>
		</template>
	</SpaceOrPageSelector>
</template>
